import './index.module.less';

import {
  Button,
  DatePicker,
  Pagination,
  Radio,
  Select,
  Table,
  TextLink,
  ToastContainer,
  Tooltip,
} from '@bfe/moly';
import { HelpIcon } from '@bfe/moly-icons';
import classNames from 'classnames';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FC, useEffect } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useUserInfo } from '@/hooks/useUserInfo';
import { useI18nContext } from '@/i18n/I18n';

import { EXPORT_STATUS_TMS, STATEMENT_DATE_TYPE } from './constants';
import useStatement from './useStatement';

dayjs.extend(utc);

const Statement: FC = () => {
  const [$t, lang] = useI18nContext();
  const { msgError, user } = useUserInfo();
  const {
    isLoading,
    isUpdating,
    exportBtnStatus,
    resultOriginData,
    resultData,
    statementDateType,
    handlerDateTypeChange,
    monthlyList,
    monthlyValues,
    handlerMonthlyChange,
    disabledDate,
    customizedTime,
    handlerCustomizedTimeChange,
    handlerPaginationChange,
    downloadIds,
    handleDownload,
    curExportCount,
    handleGenerateClick,
    currentPage,
    PAGE_SIZE,
  } = useStatement();

  // 账单时间类型切换
  const items = [
    {
      key: STATEMENT_DATE_TYPE.MONTHLY_STATEMENT,
      value: STATEMENT_DATE_TYPE.MONTHLY_STATEMENT,
      childrenNode: $t('monthlyStatement'),
    },
    {
      key: STATEMENT_DATE_TYPE.CUSTOMIZED_TIME,
      value: STATEMENT_DATE_TYPE.CUSTOMIZED_TIME,
      childrenNode: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {$t('customTime')}
          <Tooltip title={$t('specificDateStatementTips')}>
            <HelpIcon style={{ width: 14, height: 14 }} />
          </Tooltip>
        </div>
      ),
    },
  ];

  const RemainingTips = () => {
    return (
      <span className="statement-contianer-action-button-text-number">
        {curExportCount}
      </span>
    );
  };

  const columns: any[] = [
    {
      accessorKey: 'endTime',
      header: $t('statments'),
      cell: ({ row }: any) => {
        const endTime = row.getValue('endTime');
        return dayjs(endTime * 1000).format('YYYY/MM/DD');
      },
    },
    {
      accessorKey: 'submitAt',
      header: $t('applyTime'),
      cell: ({ row }: any) => {
        const submitAt = row.getValue('submitAt');
        return dayjs(submitAt * 1000).format('YYYY/MM/DD HH:mm:ss');
      },
    },
    {
      accessorKey: 'status',
      header: $t('state'),
      cell: ({ row }: any) => {
        const stateType = row.getValue('status');
        const currentTaskStatus = EXPORT_STATUS_TMS[stateType];
        if (currentTaskStatus) {
          return (
            <>
              <i
                className="export__statment-wrap-result-status-circle"
                style={{ background: currentTaskStatus?.color }}
              />
              <span className="export__statment-wrap-result-status">
                {$t(currentTaskStatus?.tms)}
              </span>
            </>
          );
        }
        return <span className="export__statment-wrap-result-status" />;
      },
    },
    {
      accessorKey: 'repositories',
      header: $t('child.operation'),
      fixed: 'right',
      cell: ({ row }: any) => {
        const status = row.getValue('status');
        const id = row.id;

        const currentTaskStatus = EXPORT_STATUS_TMS[status];
        if (currentTaskStatus?.hasDownload) {
          return (
            <TextLink
              variant="primary"
              size="normal"
              disabled={downloadIds?.includes(id)}
              onClick={() => handleDownload(row)}
            >
              {$t('download')}
            </TextLink>
          );
        }

        return (
          <span className="export__statment-wrap-remember_tagsult-msg">
            {$t(currentTaskStatus?.msg)}
          </span>
        );
      },
    },
  ];

  return (
    <div className={classNames('statement-contianer')}>
      <ToastContainer />
      <div className={classNames('statement-contianer-header')}>
        <div className={classNames('statement-contianer-title')}>
          <span data-paint>{$t('accountStatement')}</span>
        </div>
        <div className="statement-contianer-subtitle">
          <p>
            <span>1.</span>
            <span>
              {user?.member_tag?.UTA === 'SUCCESS'
                ? $t('accountStatementTips01')
                : $t('accountStatementTips02')}
            </span>
          </p>
          <p>
            <span>2.</span> <span>{$t('accountStatementTips03')}</span>
          </p>
        </div>
      </div>

      <div className={classNames('statement-contianer-operate')}>
        <div className={classNames('statement-contianer-radio')}>
          <Radio
            value={statementDateType}
            items={items}
            size="medium"
            orientation="horizontal"
            handleRadioChange={handlerDateTypeChange}
            radioName="first"
          />
        </div>
        <div className={classNames('statement-contianer-action')}>
          <div className={classNames('statement-contianer-action-date')}>
            <div
              id="STATEMENT_SELECT"
              style={{
                display:
                  statementDateType === STATEMENT_DATE_TYPE.MONTHLY_STATEMENT
                    ? 'block'
                    : 'none',
              }}
            >
              <Select
                size="small"
                mode="tags"
                allowClear
                value={monthlyValues}
                onChange={handlerMonthlyChange}
                width={'280px'}
                getPopupContainer={() => {
                  return document.getElementById(
                    'STATEMENT_SELECT',
                  ) as HTMLDivElement;
                }}
                options={monthlyList}
              />
            </div>
            <div
              style={{
                display:
                  statementDateType === STATEMENT_DATE_TYPE.CUSTOMIZED_TIME
                    ? 'block'
                    : 'none',
              }}
            >
              <DatePicker
                size="small"
                style={{ width: '280px' }}
                value={customizedTime}
                disabledDate={disabledDate}
                onChange={handlerCustomizedTimeChange}
              />
            </div>
          </div>

          <div className={classNames('statement-contianer-action-button')}>
            <Button
              size="x-small"
              variant="primary"
              style={{ minWidth: '80px', height: '40px' }}
              loading={isUpdating}
              disabled={exportBtnStatus}
              onClick={handleGenerateClick}
            >
              {$t('generateNowBtn')}
            </Button>
            <div className="statement-contianer-action-button-text">
              <Trans
                i18nKey="generateRemainingTimes"
                components={[<RemainingTips key="generateRemainingTimes" />]}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={classNames('statement-contianer-body')}>
        <Table
          className="independent-audit-report__table"
          columns={columns}
          data={resultData}
          loading={isLoading}
          rowSplitStyle="normal"
        />
        <div className="statement-contianer-report__pagination">
          <Pagination
            align="right"
            current={currentPage}
            pageSize={PAGE_SIZE}
            total={resultOriginData?.length}
            onChange={handlerPaginationChange}
          />
        </div>
      </div>
    </div>
  );
};

export default Statement;
